import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { Spin, message, Upload } from 'antd';
import App from '../Utils/LayoutMobile';

// ── Theme Constants ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_GRAD      = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

// ── Icons ──
const UserIcon = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const MailIcon = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const PhoneIcon = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
const CameraIcon = () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>;
const ShieldIcon = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;

// ── Section Label ──
function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>{children}</span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(null);

  const fetchUserProfile = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();

      const userProfile = {
        username: user.username,
        email: user.attributes?.email,
        name: user.attributes?.name,
        phone: user.attributes?.phone_number,
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        // Check if a picture attribute exists, otherwise default to null
        picture: user.attributes?.picture || null, 
      };

      setProfile(userProfile);
      if (userProfile.picture) setAvatarUrl(userProfile.picture);

    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      message.error('Unable to fetch user profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  // ── Local Image Preview Handler ──
  const handleAvatarChange = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file) {
      // Create a temporary local URL so the user sees the preview instantly
      const localUrl = URL.createObjectURL(file);
      setAvatarUrl(localUrl);
      
      // Reminder toast for backend integration
      message.info("Photo preview generated! Backend upload integration required.");
      
      // TODO: Add your AWS S3 or API upload logic here later!
      // const formData = new FormData();
      // formData.append("avatar", file);
      // await axios.post('/api/upload-avatar', formData);
    }
  };

  if (loading) {
    return (
      <App>
        <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: C_SOFT_BG }}>
          <Spin size="large" tip="Loading profile..." />
        </div>
      </App>
    );
  }

  if (!profile) {
    return (
      <App>
        <div className="lc"><div className="mw"><p style={{ color: '#ef4444' }}>User profile not available.</p></div></div>
      </App>
    );
  }

  return (
    <App>
      <div className="lc">
        <div className="mw">
          
          {/* ── Header ── */}
          <div className="header-flex">
            <div>
              <SectionLabel>Account Settings</SectionLabel>
              <h1 className="sec-title">My <span style={{ color: C_PINK }}>Profile</span></h1>
              <p className="sec-sub">Manage your personal information and account security.</p>
            </div>
          </div>

          {/* ── Profile Card ── */}
          <div className="glass profile-card">
            
            {/* Banner Strip */}
            <div className="profile-banner" />

            <div className="profile-content">
              
              {/* Left Column: Avatar & Quick Info */}
              <div className="profile-sidebar">
                <Upload
                  showUploadList={false}
                  beforeUpload={() => false} // Prevent automatic Antd upload
                  onChange={handleAvatarChange}
                  accept="image/*"
                >
                  <div className="avatar-container">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="User Avatar" className="avatar-img" />
                    ) : (
                      <div className="avatar-placeholder">
                        <span className="initials">
                          {profile.name ? profile.name.charAt(0).toUpperCase() : profile.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="avatar-overlay">
                      <CameraIcon />
                    </div>
                  </div>
                </Upload>
                
                <div className="sidebar-text">
                  <h2 className="profile-name">{profile.name || profile.username}</h2>
                  <div className="role-badge">
                    <ShieldIcon /> Administrator
                  </div>
                </div>
              </div>

              {/* Right Column: Detailed Info */}
              <div className="profile-details">
                <h3 className="details-title">Personal Information</h3>
                
                <div className="info-grid">
                  <div className="info-item">
                    <div className="info-icon"><UserIcon /></div>
                    <div className="info-text">
                      <span className="info-label">Username</span>
                      <span className="info-val">{profile.username}</span>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon" style={{ color: C_PINK, background: 'rgba(240,77,160,0.08)' }}><MailIcon /></div>
                    <div className="info-text">
                      <span className="info-label">Email Address</span>
                      <span className="info-val">{profile.email || 'Not provided'}</span>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon" style={{ color: '#16a34a', background: 'rgba(22,163,74,0.08)' }}><PhoneIcon /></div>
                    <div className="info-text">
                      <span className="info-label">Phone Number</span>
                      <span className="info-val">{profile.phone || 'Not provided'}</span>
                    </div>
                  </div>
                </div>
                
              </div>

            </div>
          </div>

        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        .lc { font-family: 'Segoe UI', sans-serif; background: ${C_SOFT_BG}; min-height: 100vh; padding: 40px 0 80px; }
        .mw { max-width: 800px; margin: 0 auto; padding: 0 24px; }

        .header-flex { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 32px; }
        .sec-title { font-size: clamp(28px, 4vw, 38px); font-weight: 800; color: ${C_TEXT_MAIN}; letter-spacing: -0.02em; line-height: 1.15; margin: 0 0 8px; }
        .sec-sub { font-size: 15px; color: ${C_TEXT_MUTED}; margin: 0; }

        /* Profile Card */
        .profile-card {
          position: relative;
          padding: 0 !important; /* Override standard glass padding to allow flush banner */
        }
        .glass { 
          background: #fff; border-radius: 20px; 
          border: 1px solid rgba(139,47,201,0.08); 
          box-shadow: 0 10px 40px rgba(0,0,0,0.04); overflow: hidden;
        }

        .profile-banner {
          height: 120px;
          /* 👇 CHANGE YOUR BACKGROUND IMAGE URL HERE 👇 */
          background: url('/your-new-bg-image.jpg') center/cover no-repeat, ${C_GRAD};
          border-bottom: 1px solid rgba(139,47,201,0.1);
        }

        .profile-content {
          display: flex;
          padding: 32px;
          gap: 40px;
          align-items: flex-start;
        }

        /* Sidebar & Avatar */
        .profile-sidebar {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: -70px; /* Pull up over the banner */
          width: 200px;
          flex-shrink: 0;
        }

        .avatar-container {
          width: 140px; height: 140px;
          border-radius: 50%;
          background: #fff;
          padding: 6px; /* White border effect */
          position: relative;
          cursor: pointer;
          border: 1px solid rgba(139,47,201,0.15);
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          overflow: hidden;
          transition: all 0.3s;
        }
        .avatar-container:hover {
          border-color: ${C_PINK};
          box-shadow: 0 12px 32px rgba(240,77,160,0.15);
        }
        .avatar-img {
          width: 100%; height: 100%;
          border-radius: 50%; object-fit: cover;
        }
        .avatar-placeholder {
          width: 100%; height: 100%;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(139,47,201,0.1), rgba(240,77,160,0.1));
          display: flex; align-items: center; justify-content: center;
        }
        .initials { font-size: 48px; font-weight: 800; color: ${C_PURPLE}; }

        .avatar-overlay {
          position: absolute; top: 6px; left: 6px; right: 6px; bottom: 6px;
          background: rgba(26,0,64,0.5);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: #fff; opacity: 0; transition: opacity 0.2s;
          backdrop-filter: blur(2px);
        }
        .avatar-container:hover .avatar-overlay { opacity: 1; }

        .sidebar-text { text-align: center; margin-top: 16px; }
        .profile-name { font-size: 20px; font-weight: 800; color: ${C_TEXT_MAIN}; margin: 0 0 6px; }
        .role-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 12px; background: rgba(139,47,201,0.08); color: ${C_PURPLE};
          border-radius: 50px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
        }

        /* Details Side */
        .profile-details {
          flex: 1;
          background: #faf8fc;
          border-radius: 16px;
          padding: 24px 32px;
          border: 1px dashed rgba(139,47,201,0.2);
        }
        .details-title {
          font-size: 16px; font-weight: 800; color: ${C_TEXT_MAIN};
          margin: 0 0 20px; padding-bottom: 12px;
          border-bottom: 1px solid rgba(139,47,201,0.08);
        }

        .info-grid {
          display: flex; flex-direction: column; gap: 20px;
        }
        .info-item {
          display: flex; align-items: center; gap: 16px;
        }
        .info-icon {
          width: 40px; height: 40px; border-radius: 10px;
          background: rgba(139,47,201,0.08); color: ${C_PURPLE};
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .info-text { display: flex; flex-direction: column; gap: 2px; }
        .info-label { font-size: 12px; font-weight: 600; color: ${C_TEXT_MUTED}; text-transform: uppercase; letter-spacing: 0.05em; }
        .info-val { font-size: 15px; font-weight: 700; color: ${C_TEXT_MAIN}; }

        @media(max-width: 768px) {
          .profile-content { flex-direction: column; align-items: center; padding: 24px 20px; gap: 24px; }
          .profile-details { width: 100%; padding: 20px; }
          .info-item { flex-direction: column; align-items: flex-start; gap: 10px; background: #fff; padding: 16px; border-radius: 12px; border: 1px solid rgba(139,47,201,0.06); }
        }
      `}</style>
    </App>
  );
};

export default UserProfile;